import React, { useEffect, useRef, useState } from "react";
import { NavLink, useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faEye,
  faFileExport,
  faImage,
  faMusic,
  faPalette,
  faPlay,
  faSignInAlt,
  faSignOutAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../contexts/AuthContext";
import { useSession } from "../contexts/SessionContext";
import { ROUTES } from "../routes/routes";
import ReportIssueModal from "./ReportIssueModal";
import "./css/Navigation.css";

const Navigation = () => {
  const { isAuthenticated, isAdmin, logout, user } = useAuth();
  const { currentSession, clearSession } = useSession();
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuVisible, setIsMenuVisible] = useState(() => window.innerWidth > 1024);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 1024);
  const [showReportIssue, setShowReportIssue] = useState(false);
  const wasMobileRef = useRef(window.innerWidth <= 1024);

  const handleStartOver = () => {
    clearSession();
    navigate(ROUTES.Home());
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      const wasMobile = wasMobileRef.current;
      wasMobileRef.current = mobile;
      setIsMobile(mobile);
      // Only act when we cross the breakpoint. "Visible" means a permanent
      // sidebar on desktop but a full-screen drawer on mobile, so carrying the
      // desktop value across leaves an open drawer covering the whole page.
      // Resizing without crossing must not close a drawer the user opened,
      // since iOS fires resize whenever the URL bar or keyboard moves.
      if (mobile !== wasMobile) {
        setIsMenuVisible(!mobile);
      }
    };
    
    const handleClickOutside = (event) => {
      if (isMobile && isMenuVisible) {
        const target = event.target;
        const menu = document.querySelector('.menu');
        const toggle = document.querySelector('.mobile-menu-toggle');
        const headerToggle = document.querySelector('.page-header-menu-button');
        
        // Don't close if clicking inside the menu
        if (menu && menu.contains(target)) {
          return;
        }
        
        // Don't close if clicking the toggle button
        if (toggle && (toggle.contains(target) || target === toggle)) {
          return;
        }
        
        // Don't close if clicking the header toggle button
        if (headerToggle && (headerToggle.contains(target) || target === headerToggle)) {
          return;
        }
        
        // Close menu if clicking outside
        setIsMenuVisible(false);
      }
    };

    const handleToggleMenu = () => {
      setIsMenuVisible(prev => !prev);
    };
    
    window.addEventListener("resize", handleResize);
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    window.addEventListener("toggleNavigationMenu", handleToggleMenu);
    handleResize();
    
    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      window.removeEventListener("toggleNavigationMenu", handleToggleMenu);
    };
  }, [isMenuVisible, isMobile]);

  const toggleMenuVisibility = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsMenuVisible(prev => !prev);
  };

  const MenuItem = ({ icon, label, to, action = null, onClick }) => {
    const handleItemClick = (e) => {
      // Only handle clicks for non-NavLink items
      if (onClick) {
        e.preventDefault();
        e.stopPropagation();
        onClick();
        // Close menu on mobile after clicking
        if (isMobile) {
          setIsMenuVisible(false);
        }
      } else if (action) {
        e.preventDefault();
        e.stopPropagation();
        action();
        // Close menu on mobile after clicking
        if (isMobile) {
          setIsMenuVisible(false);
        }
      }
      // For NavLink items, don't prevent default - let NavLink handle it
    };

    const handleNavLinkClick = (e) => {
      // Close menu on mobile when NavLink is clicked
      if (isMobile) {
        // Small delay to allow navigation to start before closing menu
        setTimeout(() => {
          setIsMenuVisible(false);
        }, 100);
      }
      // Let NavLink handle navigation normally
    };

    return (
      <li 
        onClick={to ? undefined : handleItemClick}
        onTouchEnd={to ? undefined : (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleItemClick(e);
        }}
      >
        <div className="menu-item-header">
          <FontAwesomeIcon icon={icon} />
          {to ? (
            <NavLink 
              to={to} 
              className="menu-link"
              onClick={handleNavLinkClick}
            >
              {label}
            </NavLink>
          ) : (
            <span className="menu-link">{label}</span>
          )}
        </div>
      </li>
    );
  };

  const renderMenuItems = () => {
    // If we have a session (design selected), show the design workflow
    if (currentSession || sessionId) {
      const activeSessionId = sessionId || currentSession?.sessionId;
      return (
        <>
          <MenuItem icon={faImage} label="Images" to={ROUTES.SessionImages(activeSessionId)} />
          <MenuItem icon={faPalette} label="Colours" to={ROUTES.SessionColours(activeSessionId)} />
          <MenuItem icon={faMusic} label="Audio" to={ROUTES.SessionAudio(activeSessionId)} />
          <MenuItem icon={faEye} label="Preview" to={ROUTES.SessionPreview(activeSessionId)} />
          <MenuItem icon={faFileExport} label="Export" to={ROUTES.SessionExport(activeSessionId)} />
          <MenuItem icon={faPlay} label="Start Over" onClick={handleStartOver} />
        </>
      );
    }

    // No design selected yet - show design selection
    return (
      <>
        <MenuItem icon={faPlay} label="Choose Design" to={ROUTES.Home()} />
      </>
    );
  };

  // Only show navigation if there's an active session (design selected)
  // Also hide on landing page, login, signup, and forgot password pages
  // Show on settings page but menu items will only show when there's a session
  const isLandingPage = location.pathname === '/';
  const isAuthPage = location.pathname.includes('/auth/');
  
  if (isLandingPage || isAuthPage) {
    return null;
  }

  return (
    <>
      {/* Mobile menu toggle button - always visible on mobile when menu is closed */}
      {isMobile && !isMenuVisible && (
        <button 
          className="mobile-menu-toggle"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleMenuVisibility(e);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleMenuVisibility(e);
          }}
          aria-label="Open menu"
          type="button"
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
      )}
      
      {/* Overlay when menu is open on mobile */}
      {isMobile && isMenuVisible && (
        <div 
          className="menu-overlay"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsMenuVisible(false);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsMenuVisible(false);
          }}
          aria-label="Close menu"
        />
      )}
      
      <div className={`menu ${isMenuVisible ? 'menu-open' : ''}`}>
        <div className="header">
          <NavLink to={ROUTES.Home()} className="header-brand-link">
            <img src="/white_logo.png" alt="Render FM Logo" className="header-logo" />
            <h1>Render FM</h1>
          </NavLink>
          <div 
            className="burger-menu" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleMenuVisibility(e);
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleMenuVisibility(e);
            }}
          >
            <FontAwesomeIcon icon={faBars} />
          </div>
        </div>
      <div className="menu-top-container">
        <nav>
          <ul className="menu-top">{renderMenuItems()}</ul>
        </nav>
      </div>
      <div className="menu-footer">
        <nav>
          <ul className="menu-bottom">
            {isAuthenticated ? (
              <MenuItem
                icon={faSignOutAlt}
                label="Log Out"
                action={logout}
              />
            ) : (
              <MenuItem
                icon={faSignInAlt}
                label="Log in"
                to={ROUTES.Login()}
              />
            )}
          </ul>
        </nav>
        {isAdmin && (
          <button
            type="button"
            className="menu-report-issue"
            onClick={() => {
              setShowReportIssue(true);
              if (isMobile) {
                setIsMenuVisible(false);
              }
            }}
          >
            Report an issue
          </button>
        )}
      </div>
    </div>
    {showReportIssue && isAdmin && (
      <ReportIssueModal
        onClose={() => setShowReportIssue(false)}
        defaultEmail={user?.email || ""}
      />
    )}
    </>
  );
};

export default Navigation;
